import { DashboardLayout } from "@/components/dashboard/dashboard-layout"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Label } from "@/components/ui/label"
import { Checkbox } from "@/components/ui/checkbox"
import { useParams, useNavigate } from "react-router-dom"
import { useState, useEffect } from "react"
import * as React from "react"
import { useCreatePackagePurchaseQR, useCheckPackagePurchaseStatus, useAvailablePackages } from "@/lib/api/package-purchase"
import { ActivationChoice } from "@/src/types/package-purchase"
import { AlertCircle, CheckCircle, Loader2, ArrowLeft, Clock, Package as PackageIcon, Calendar, Info, Zap } from "lucide-react"
import { toast } from "sonner"

export default function PurchasePackagePage() {
  const { packageId } = useParams<{ packageId: string }>()
  const navigate = useNavigate()

  const [step, setStep] = useState<'confirmation' | 'qr' | 'success'>('confirmation')
  const [transactionId, setTransactionId] = useState<string | null>(null)
  const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null)
  const [qrTimeout, setQrTimeout] = useState<number>(900) // 15 minutes
  const [activationType, setActivationType] = useState<'immediate' | 'queued'>('immediate')
  const [currentPackageExpiry, setCurrentPackageExpiry] = useState<string | null>(null)
  // Activation choice states (for users with active paid package)
  const [activationChoice, setActivationChoice] = useState<ActivationChoice>('queued')
  const [immediateSwitchAccepted, setImmediateSwitchAccepted] = useState(false)
  const [effectiveChoice, setEffectiveChoice] = useState<ActivationChoice>('queued')

  // API hooks
  const createQRMutation = useCreatePackagePurchaseQR()
  const { data: statusData } = useCheckPackagePurchaseStatus(transactionId)
  const { data: packagesData, isLoading: isLoadingPackages } = useAvailablePackages()

  // Get package info from API data instead of mock
  const packageInfo = React.useMemo(() => {
    if (!packagesData?.availablePackages || !packageId) return undefined
    
    const pkg = packagesData.availablePackages.find(p => p.id === packageId)
    if (!pkg) return undefined
    
    // Transform to expected format
    return {
      id: pkg.id,
      name: pkg.name,
      displayName: pkg.display_name || pkg.name,
      monthlyPrice: parseFloat(pkg.price_monthly || '0'),
      includedCredits: pkg.included_credits,
      features: pkg.features || []
    }
  }, [packagesData, packageId])

  useEffect(() => {
    if (!isLoadingPackages && !packageInfo && packageId) {
      toast.error('ไม่พบแพ็กเกจที่เลือก')
      navigate('/settings/packages')
    }
  }, [packageInfo, packageId, isLoadingPackages, navigate])

  // Monitor transaction status
  useEffect(() => {
    if (statusData?.data?.isCompleted) {
      setStep('success')
      toast.success('ยืนยันการชำระเงินสำเร็จ!')
    } else if (statusData?.data?.isFailed) {
      toast.error('การชำระเงินล้มเหลว')
      setStep('confirmation')
      setTransactionId(null)
      setQrCodeUrl(null)
    }
  }, [statusData])

  // QR countdown timer
  useEffect(() => {
    if (step !== 'qr' || !transactionId) return

    const timer = setInterval(() => {
      setQrTimeout((prev) => {
        if (prev <= 1) {
          toast.info('หมดเวลาแสดง QR Code แล้ว')
          setStep('confirmation')
          setTransactionId(null)
          setQrCodeUrl(null)
          return 900
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [step, transactionId])

  // true when user has an active paid (non-starter) package
  const hasActivePaidPackage = React.useMemo(() => {
    const cur = packagesData?.currentPackage
    return !!cur && cur.name !== 'starter'
  }, [packagesData])

  const handleConfirmPurchase = async () => {
    if (!packageInfo) return

    try {
      // Determine effective activation choice
      // Starter users always get immediate; paid-package users respect their choice + consent
      const resolvedChoice: ActivationChoice = hasActivePaidPackage ? activationChoice : 'immediate'
      const accepted = resolvedChoice === 'immediate' ? immediateSwitchAccepted : false

      const result = await createQRMutation.mutateAsync({
        packageId: packageInfo.id,
        activationChoice: resolvedChoice,
        immediateSwitchAccepted: accepted,
        consentVersion: '1.0'
      })

      // Store effective choice returned from backend (may differ from requested)
      const backendEffective = result.data.activationTiming.effectiveChoice
      const resolvedEffective: ActivationChoice =
        backendEffective ?? (result.data.activationTiming.isImmediate ? 'immediate' : 'queued')

      setEffectiveChoice(resolvedEffective)
      setActivationType(resolvedEffective)
      setTransactionId(result.data.transaction.id)
      setQrCodeUrl(result.data.qrCode.qr_code)
      setCurrentPackageExpiry(result.data.activationTiming.currentPackageExpiresAt || null)
      setStep('qr')
      toast.success('สร้าง QR Code สำเร็จ')

    } catch (error: any) {
      if (error.code === 'PENDING_PACKAGE_EXISTS') {
        toast.error('คุณมีแพ็กเกจรออยู่แล้ว กรุณารอจนกว่าจะเริ่มใช้งานก่อนซื้อใหม่')
      } else if (error.code === 'SAME_PACKAGE') {
        toast.error('คุณใช้แพ็กเกจนี้อยู่แล้ว')
      } else {
        toast.error(`เกิดข้อผิดพลาด: ${error.message}`)
      }
    }
  }

  const handlePaymentSuccess = () => {
    // Set step to success to show completion message
    setStep('success')
    // Show success notification
    toast.success("✅ ยืนยันการชำระเงินแล้ว", {
      description: "ขอบคุณที่แจ้งยืนยัน! เจ้าหน้าที่จะตรวจสอบการชำระเงินและอัพเกรดแพ็กเกจให้ภายใน 24 ชั่วโมง",
    })
  }

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  if (!packageInfo) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      </DashboardLayout>
    )
  }

  // Step 1: Confirmation
  if (step === 'confirmation') {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/settings/packages')}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              ย้อนกลับ
            </Button>
            <h1 className="text-2xl font-bold">ยืนยันการซื้อแพ็กเกจ</h1>
          </div>

          <div className="max-w-2xl mx-auto space-y-6">
            {/* Package Details Card */}
            <Card className="p-6">
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                    <PackageIcon className="w-6 h-6 text-primary" />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-xl font-bold">{packageInfo.displayName}</h2>
                    <p className="text-3xl font-bold text-primary mt-2">
                      ฿{packageInfo.monthlyPrice.toLocaleString()}
                      <span className="text-base text-gray-600 font-normal">/เดือน</span>
                    </p>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <h3 className="font-semibold mb-3">สิ่งที่คุณจะได้รับ:</h3>
                  <ul className="space-y-2">
                    {packageInfo.features.map((feature: string, idx: number) => (
                      <li key={idx} className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Activation Timing Info — shown only for starter users (no choice needed) */}
                {!hasActivePaidPackage && (
                <div className="border-t pt-4">
                  <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <Calendar className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-blue-900">
                        ⚡ เริ่มใช้งานทันที
                      </p>
                    </div>
                  </div>
                </div>
                )}

                {/* Activation Choice Selector — shown only when user has active paid package */}
                {hasActivePaidPackage && (
                <div className="border-t pt-4 space-y-4">
                  <h3 className="font-semibold text-gray-900">เลือกเวลาเริ่มใช้งานแพ็กเกจใหม่</h3>

                  <RadioGroup
                    value={activationChoice}
                    onValueChange={(val) => {
                      setActivationChoice(val as ActivationChoice)
                      if (val === 'queued') setImmediateSwitchAccepted(false)
                    }}
                    className="space-y-3"
                  >
                    {/* Queued option (recommended) */}
                    <div className={`flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                      activationChoice === 'queued'
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                      onClick={() => {
                        setActivationChoice('queued')
                        setImmediateSwitchAccepted(false)
                      }}
                    >
                      <RadioGroupItem value="queued" id="queued" className="mt-1" />
                      <div className="flex-1">
                        <Label htmlFor="queued" className="font-semibold text-gray-900 cursor-pointer">
                          📋 รอจนกว่าแพ็กเกจปัจจุบันจะหมดอายุ{' '}
                          <span className="text-xs text-blue-600 font-medium">[แนะนำ]</span>
                        </Label>
                        <p className="text-sm text-gray-600 mt-1">
                          ใช้งาน {packagesData?.currentPackage?.name} ต่อได้จนหมดอายุ
                          {packagesData?.currentPackage?.days_remaining
                            ? ` (อีก ${packagesData.currentPackage.days_remaining} วัน)`
                            : ''}
                          {' '}จากนั้นแพ็กเกจใหม่จะเริ่มโดยอัตโนมัติ
                        </p>
                      </div>
                    </div>

                    {/* Immediate option */}
                    <div className={`flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                      activationChoice === 'immediate'
                        ? 'border-orange-400 bg-orange-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                      onClick={() => setActivationChoice('immediate')}
                    >
                      <RadioGroupItem value="immediate" id="immediate" className="mt-1" />
                      <div className="flex-1">
                        <Label htmlFor="immediate" className="font-semibold text-gray-900 cursor-pointer flex items-center gap-2">
                          <Zap className="w-4 h-4 text-orange-500" />
                          เริ่มใช้งานทันทีหลังอนุมัติ
                        </Label>
                        <p className="text-sm text-gray-600 mt-1">
                          แพ็กเกจปัจจุบันจะถูกยกเลิก และเครดิตคงเหลือจากแพ็กเกจเก่าจะหมดอายุ
                        </p>
                      </div>
                    </div>
                  </RadioGroup>

                  {/* Consent checkbox — shown only when immediate is selected */}
                  {activationChoice === 'immediate' && (
                    <div className="p-4 bg-orange-50 rounded-lg border border-orange-300 space-y-3">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                        <div className="text-sm text-orange-900">
                          <p className="font-semibold mb-1">⚠️ สิ่งที่จะเกิดขึ้นเมื่อสลับทันที:</p>
                          <ul className="list-disc list-inside space-y-1 text-orange-800">
                            <li>แพ็กเกจ {packagesData?.currentPackage?.name} ปัจจุบันจะถูกยกเลิกทันที</li>
                            <li>เครดิตคงเหลือที่ได้จากแพ็กเกจเดิมจะหมดอายุ (เครดิตที่ซื้อเพิ่มยังคงอยู่)</li>
                            <li>แพ็กเกจ {packageInfo?.displayName} จะเริ่มใช้งานทันทีหลังอนุมัติ</li>
                            <li>ไม่สามารถย้อนกลับได้</li>
                          </ul>
                        </div>
                      </div>
                      <div className="flex items-start gap-3 pt-1">
                        <Checkbox
                          id="immediateSwitchConsent"
                          checked={immediateSwitchAccepted}
                          onCheckedChange={(checked) => setImmediateSwitchAccepted(checked === true)}
                          className="mt-0.5"
                        />
                        <Label
                          htmlFor="immediateSwitchConsent"
                          className="text-sm font-medium text-orange-900 cursor-pointer leading-5"
                        >
                          ฉันเข้าใจและยอมรับเงื่อนไขการสลับแพ็กเกจทันที รวมถึงการสูญเสียเครดิตคงเหลือจากแพ็กเกจเดิม
                        </Label>
                      </div>
                    </div>
                  )}
                </div>
                )}

                {/* Important Notice */}
                <div className="border-t pt-4">
                  <div className="flex items-start gap-3 p-4 bg-amber-50 rounded-lg border border-amber-200">
                    <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-semibold text-amber-900">⚠️ นโยบายการคืนเงิน</p>
                      <p className="text-amber-800 mt-1">
                        การซื้อแพ็กเกจไม่สามารถยกเลิกหรือขอคืนเงินได้ กรุณาตรวจสอบรายละเอียดก่อนยืนยันการชำระเงิน
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </Card>

            {/* Confirm Button */}
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => navigate('/settings/packages')}
                className="px-10 py-6 text-lg font-semibold"
              >
                ยกเลิก
              </Button>
              <Button
                onClick={handleConfirmPurchase}
                disabled={
                  createQRMutation.isPending ||
                  (hasActivePaidPackage && activationChoice === 'immediate' && !immediateSwitchAccepted)
                }
                className="px-10 py-6 text-lg font-bold bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
              >
                {createQRMutation.isPending ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    กำลังสร้าง QR Code...
                  </>
                ) : (
                  'ยืนยันและชำระเงิน'
                )}
              </Button>
            </div>
          </div>
        </div>

      </DashboardLayout>
    )
  }

  // Step 2: QR Code Display
  if (step === 'qr') {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setStep('confirmation')
                setTransactionId(null)
                setQrCodeUrl(null)
              }}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              ย้อนกลับ
            </Button>
            <h1 className="text-2xl font-bold">ชำระเงิน</h1>
          </div>

          <div className="max-w-3xl mx-auto space-y-6">
            <Card className="p-8">
              <div className="space-y-6">
                <div className="text-center space-y-2">
                  <h2 className="text-2xl font-bold text-primary">
                    สแกน QR Code เพื่อชำระเงิน
                  </h2>
                  <p className="text-gray-600">
                    แพ็กเกจ: {packageInfo.displayName} - ฿{packageInfo.monthlyPrice.toLocaleString()}
                  </p>
                </div>

                {qrCodeUrl && (
                  <div className="flex justify-center">
                    <img
                      src={qrCodeUrl}
                      alt="Package Payment QR Code"
                      className="w-64 h-64 border-2 border-gray-300 rounded-lg p-2 bg-white"
                    />
                  </div>
                )}

                {/* Countdown Timer */}
                <div className="flex items-center justify-center gap-2 text-lg">
                  <Clock className="w-5 h-5 text-gray-600" />
                  <span className="font-mono font-semibold">
                    {formatTime(qrTimeout)}
                  </span>
                  <span className="text-gray-600">นาที</span>
                </div>

                {/* Instructions */}
                <div className="space-y-4 p-4 bg-amber-50 rounded-lg border-2 border-amber-300">
                  <div className="flex gap-3">
                    <Info className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-amber-900">📱 ขั้นตอนต่อไป:</p>
                      <ol className="text-sm text-amber-800 mt-2 space-y-1 list-decimal list-inside">
                        <li>สแกน QR Code ด้วยแอปธนาคาร</li>
                        <li>ชำระเงินให้เรียบร้อย</li>
                        <li>บันทึกหรือถ่ายภาพสลิปการโอนเงิน</li>
                        <li>ส่งสลิปเข้า LINE Official Account เพื่อให้เจ้าหน้าที่ตรวจสอบ</li>
                      </ol>
                    </div>
                  </div>
                </div>

                {/* Activation Type Info */}
                <div className="space-y-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex gap-3">
                    <Calendar className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-blue-900">
                        {activationType === 'immediate'
                          ? '⚡ เริ่มใช้งานทันทีหลังอนุมัติ'
                          : '📋 เริ่มใช้งานเมื่อแพ็กเกจปัจจุบันหมดอายุ'}
                      </p>
                      {activationType === 'queued' && currentPackageExpiry && (
                        <p className="text-sm text-blue-800 mt-1">
                          จะเริ่มใช้งานประมาณวันที่ {new Date(currentPackageExpiry).toLocaleDateString('th-TH')}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Status Message */}
                <div className="text-center">
                  <p className="text-sm text-gray-600">
                    🔍 กำลังตรวจสอบสถานะการชำระเงิน...
                  </p>
                </div>
              </div>
            </Card>

            <div className="flex justify-center gap-4">
              <Button
                variant="outline"
                onClick={() => {
                  setStep('confirmation')
                  setTransactionId(null)
                  setQrCodeUrl(null)
                }}
              >
                ยกเลิก
              </Button>
              <Button
                onClick={handlePaymentSuccess}
                className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-semibold px-8 py-3 rounded-lg shadow-lg transform hover:scale-105 transition-all duration-200"
              >
                ✅ ชำระเงินสำเร็จแล้ว
              </Button>
            </div>
          </div>
        </div>
      </DashboardLayout>
    )
  }

  // Step 3: Success
  return (
    <DashboardLayout>
      <div className="space-y-6">
        <h1 className="text-2xl font-bold">ชำระเงินสำเร็จ</h1>

        <div className="max-w-2xl mx-auto space-y-6">
          <Card className="p-8">
            <div className="space-y-6 text-center">
              <div className="flex justify-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-10 h-10 text-green-600" />
                </div>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-green-600 mb-2">
                  ยืนยันการชำระเงินสำเร็จ!
                </h2>
                <p className="text-gray-600">
                  คุณได้ชำระเงินสำหรับแพ็กเกจ {packageInfo.displayName} เรียบร้อยแล้ว
                </p>
              </div>

              <div className="border-t pt-6 space-y-3 text-left">
                <div className="flex justify-between">
                  <span className="text-gray-600">แพ็กเกจ:</span>
                  <span className="font-semibold">{packageInfo.displayName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">ราคา:</span>
                  <span className="font-semibold">฿{packageInfo.monthlyPrice.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">เครดิตที่ได้รับ:</span>
                  <span className="font-semibold">{packageInfo.includedCredits.toLocaleString()} เครดิต</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">สถานะ:</span>
                  <span className="font-semibold text-green-600">
                    {effectiveChoice === 'immediate' ? 'เริ่มใช้งานแล้ว' : 'รออนุมัติ'}
                  </span>
                </div>
              </div>

              {effectiveChoice === 'queued' && (
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 text-left">
                  <p className="text-sm text-blue-900">
                    <strong>📋 หมายเหตุ:</strong> แพ็กเกจนี้จะเริ่มใช้งานเมื่อแพ็กเกจปัจจุบันของคุณหมดอายุ
                  </p>
                </div>
              )}
            </div>
          </Card>

          <div className="flex justify-center">
            <Button onClick={() => navigate('/settings/packages')}>
              กลับไปหน้าแพ็กเกจ
            </Button>
          </div>
        </div>
      </div>
    </DashboardLayout>
  )
}
